"use client";

import { type DragEvent, type TouchEvent, useState } from "react";

import Image from "next/image";
import Link from "next/link";

import { ROUTES } from "@unreserved/constants/routes";
import { Drawer, DrawerClose, DrawerContent, DrawerTitle } from "@unreserved/ui/components/drawer";
import { Download } from "lucide-react";

import { Button } from "@/app/_components/shared/button";
import { IMAGES } from "@/app/_libs/constants/images";
import { useIsMobile } from "@/hooks/use-mobile";

import { LandingHeaderNavbar } from "./landing-navbar";

interface LandingHeaderProps {
  isLoggedIn: boolean;
}

export function LandingHeader({ isLoggedIn }: LandingHeaderProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleToggleMenu = () => {
    setIsMobileMenuOpen((prev) => !prev);
  };

  return (
    <>
      <LandingHeaderNavbar onToggleMobileMenu={handleToggleMenu} isLoggedIn={isLoggedIn} />

      {useIsMobile() && (
        <Drawer open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen} direction="right">
          <DrawerContent
            className="bg-background-0 [&_[data-vaul-drag-handle]]:hidden"
            style={{
              width: "100%",
              height: "calc(100vh - 69.5px)",
              top: "69.5px",
              bottom: "auto",
            }}
            onDragStart={(e: DragEvent<HTMLDivElement>) => e.preventDefault()}
            onTouchStart={(e: TouchEvent<HTMLDivElement>) => {
              // Prevent drag-to-close
              const target = e.target as HTMLElement;
              if (target.closest("[data-vaul-drag-handle]")) {
                e.preventDefault();
              }
            }}>
            <DrawerTitle></DrawerTitle>
            <div className="flex h-full flex-col overflow-y-auto px-4 pt-4 pb-6 sm:px-6">
              <div className="bg-background-100 mb-5 rounded-xl px-4 py-5">
                <div className="flex flex-col items-center gap-3">
                  <div className="relative h-10 w-[130px]">
                    <Image
                      src={IMAGES.LOGO}
                      alt="Unreserved Logo"
                      width={130}
                      height={32}
                      className="h-7 w-44.75"
                      objectFit="contain"
                    />
                  </div>
                  <p className="text-base font-medium text-gray-900">Real Estate Redefined</p>
                  {isLoggedIn ? (
                    <DrawerClose asChild>
                      <Button
                        className="bg-primary-700 hover:bg-primary-700/90 h-9 px-6 text-white"
                        asChild>
                        <Link href={ROUTES.DASHBOARD.HOME}>Back to dashboard</Link>
                      </Button>
                    </DrawerClose>
                  ) : (
                    <div className="flex gap-3">
                      <DrawerClose asChild>
                        <Button
                          variant="outline"
                          className="h-9 border-black px-6 text-black hover:bg-black hover:text-white"
                          asChild>
                          <Link href={ROUTES.AUTH.LOGIN}>login</Link>
                        </Button>
                      </DrawerClose>
                      <DrawerClose asChild>
                        <Button
                          className="bg-primary-700 hover:bg-primary-700/90 h-9 px-6 text-white"
                          asChild>
                          <Link href={ROUTES.AUTH.SIGNUP}>Sign Up</Link>
                        </Button>
                      </DrawerClose>
                    </div>
                  )}
                </div>
              </div>

              <nav className="flex flex-col gap-8 px-0 text-base font-medium text-black">
                <DrawerClose asChild>
                  <Link href="#about" className="hover:text-primary-700 transition-colors">
                    About Us
                  </Link>
                </DrawerClose>
                <DrawerClose asChild>
                  <Link href="#features" className="hover:text-primary-700 transition-colors">
                    Features
                  </Link>
                </DrawerClose>
                <DrawerClose asChild>
                  <Link href="#models" className="hover:text-primary-700 transition-colors">
                    Models
                  </Link>
                </DrawerClose>
                <DrawerClose asChild>
                  <Button
                    variant="default"
                    className="mt-auto flex w-full items-center justify-between rounded-md bg-black px-5 py-3 text-base font-normal text-white"
                    asChild>
                    <Link
                      href="#download"
                      className="flex w-full items-center justify-between scroll-smooth">
                      <span className="text-left">Download App</span>
                      <Download className="h-5 w-5" aria-hidden="true" />
                    </Link>
                  </Button>
                </DrawerClose>
              </nav>
            </div>
          </DrawerContent>
        </Drawer>
      )}
    </>
  );
}
